import { useRouter } from '@uirouter/react';
import { useImmerAtom } from 'jotai-immer';
import { editModelAtom } from '../vendor_edit_atoms';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import utilSvc from '../../../../core/utilSvc';
import validSvc from '../../../../core/validSvc';
import { HttpError422, HttpErrorUpsert } from '../../../../../global';
import { ROUTE_LIST } from '../../vendor_const';
import { AdminVendorModel } from '../../vendor_types';
import { useCallback } from 'react';
import { useVendorEditStore } from './useVendorEditStore';
import { upsert } from '../../data/vendorRepo';
import { ValidPropText } from '../../../../core/components/validation/ValidInputText';
import { ValidPropRadioText } from '../../../../core/components/validation/ValidInputRadioText';
import { ValidPropDroplistText } from '../../../../core/components/validation/ValidInputDroplistText';

type UseVendorEditModelReturn = {
    model: AdminVendorModel;
    setPropText: (prop: string, val: string | number) => void;
    setPropList: (prop: string, val: string[]) => void;
    submit: () => boolean;
    waiting: boolean;
    setWait: (wait: boolean) => void;
    cancelEdit: () => void;
};

export const useVendorEditModel = (): UseVendorEditModelReturn => {
    const router = useRouter();
    const { waiting, setWait, state } = useVendorEditStore();
    const [model, setModel] = useImmerAtom(editModelAtom);

    const setPropText = useCallback(
        (prop: string, val: string | number): void => {
            setModel(draft => {
                draft[prop].val = val;
            });
        },
        [setModel],
    );

    const setPropList = useCallback(
        (prop: string, val: string[]): void => {
            setModel(draft => {
                draft[prop].val = val;
            });
        },
        [setModel],
    );

    const cancelEdit = useCallback((): void => {
        router.stateService.go(ROUTE_LIST);
    }, [router.stateService]);

    // AI AGENT: consolidate into validModel
    /*
    const validateWholesaler = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            const name = validateFieldByLength({ prop: draft.Name, required: true, minLength: 3, maxLength: 50 });
            const senderId = validateFieldByLength({
                prop: draft.SenderId,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? false : true,
            });
            const receiverId = validateFieldByLength({
                prop: draft.ReceiverId,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? false : true,
            });
            const venId = validateFieldByLength({
                prop: draft.Id,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? true : false,
            });

            if (!name || !receiverId || !senderId || !venId) {
                result = false;
            }
        });
        return result;
    }, [setModel, state.mode]);
    */

    // AI AGENT: consolidate into validModel
    /*    
    const validateManufacturer = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            const name = validateFieldByLength({ prop: draft.Name, required: true, minLength: 3, maxLength: 50 });
            const senderId = validateFieldByLength({
                prop: draft.SenderId,
                minLength: 1,
                maxLength: 30,
                required: true,
            });
            const receiverId = validateFieldByLength({
                prop: draft.ReceiverId,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? false : true,
            });
            const creditReplenishId = validateFieldByLength({
                prop: draft.CreditReplenishId,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? false : true,
            });
            const venId = validateFieldByLength({
                prop: draft.Id,
                minLength: 1,
                maxLength: 30,
                required: state.mode === 'CREATE' ? true : false,
            });

            if (!name || !receiverId || !senderId || !creditReplenishId || !venId) {
                result = false;
            }
        });
        return result;
    }, [setModel, state.mode]);
    */

    // AI AGENT: consolidate into validModel
    /*
    const validatePBM = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            const name = validateFieldByLength({ prop: draft.Name, required: true, minLength: 3, maxLength: 50 });

            const venId = validateFieldByLength({
                prop: draft.Id,
                minLength: 3,
                maxLength: 50,
                required: state.mode === 'CREATE' ? true : false,
            });

            const binItems = validateFieldByQuantity({
                prop: draft.PBMBINItems,
                minQuantity: 1,
                required: state.mode === 'CREATE' ? true : false,
            });

            if (!name || !venId || !binItems) {
                result = false;
            }
        });
        return result;
    }, [setModel, state.mode]);
    */

    // AI AGENT: consolidate into validModel
    /*
    const validateTPA = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            const name = validateFieldByLength({ prop: draft.Name, required: true, minLength: 3, maxLength: 50 });

            const venId = validateFieldByLength({
                prop: draft.Id,
                minLength: 3,
                maxLength: 50,
                required: state.mode === 'CREATE' ? true : false,
            });

            if (!name || !venId) {
                result = false;
            }
        });
        return result;
    }, [setModel, state.mode]);
    */

    const submitAsync = useCallback(async (): Promise<void> => {
        // API: CREATE / UPDATE
        setWait(true);
        const r = await upsert(model, state.mode);
        setWait(false);

        // ok
        if (r.status == 200) {
            ngDelegate.ok(state.mode === 'CREATE' ? 'Vendor Created' : 'Vendor Updated');
            return;
        }

        // ignore
        const err = r as HttpErrorUpsert;
        if (utilSvc.http.isIgnore(err)) {
            return;
        }

        // handle 422 as model
        if (err.status === 422) {
            setModel(draft => {
                return validSvc.http422ToModelType<AdminVendorModel>(err as HttpError422, draft);
            });
        }

        ngDelegate.ok('Cannot Save', 'Check Properties');
    }, [model, setModel, setWait, state.mode]);

    // AI AGENT: build a simple text validator with optional min/max
    function validText(val: any, min?: number, max?: number): string {
        if (!validSvc.isText(val)) {
            return 'required';
        }
        val = val.toString().trim();

        let result = '';
        if (min !== undefined && val.length < min) {
            result = `min ${min}`;
        }
        if (max !== undefined && val.length > max) {
            result += result.length > 0 ? ', and ' : '';
            result += `max ${max}`;
        }

        return result.length > 0 ? result + ' characters' : '';
    }

    // AI AGENT: helper to clear model types
    function clearValidPropText(prop: ValidPropText, val?: string): void {
        prop.err = '';
        prop.val = !!val ? val : '';
    }
    function clearValidRadioText(prop: ValidPropRadioText, val?: string): void {
        prop.err = '';
        prop.val = !!val ? val : '';
    }
    function clearValidPropDroplist(prop: ValidPropDroplistText, val?: string): void {
        prop.err = '';
        prop.val = !!val ? val : '';
    }

    // AI AGENT: convert Angular modelValid below into React AdminVendorModel by consolidating all validation functions
    const modelValid = useCallback((): boolean => {
        const action = state.mode;
        let result = false;

        // validate for both create and update
        setModel(m => {
            // Nickname - required on create, alphanumeric
            if (action == 'CREATE') {
                m.Id.err = validText(m.Id.val, 3, 15);
                if (m.Id.err == '') {
                    const regexId = /[^A-Z0-9]/;
                    if (regexId.test(m.Id.val.toUpperCase())) {
                        m.Id.err = 'Must be letters and numbers only';
                    }
                }
            }

            // Name - required
            m.Name.err = validText(m.Name.val, 2, 100);

            // ven Type - required
            // m.Type.err = validText(m.Type.val); dropdown, always has value

            // SupportsEDI
            if (m.SupportsEDI.val == 'true') {
                // Sender / Receiver ids, required
                m.SenderId.err = validText(m.SenderId.val, 1, 30);
                m.ReceiverId.err = validText(m.ReceiverId.val, 1, 30);

                // csos receiver id - required if true
                m.CSOSReceiverId.err = '';
                if (m.CSOSSplit.val == 'true') {
                    m.CSOSReceiverId.err = validText(m.CSOSReceiverId.val, 1, 30);
                } else {
                    m.CSOSReceiverId.val = '';
                }

                // csos sender id - validate if not empty, optional
                m.CSOSSenderId.err = '';
                if (m.CSOSSplit.val == 'true' && validSvc.isText(m.CSOSSenderId.val)) {
                    m.CSOSSenderId.err = validText(m.CSOSSenderId.val, 1, 30);
                } else {
                    m.CSOSSenderId.val = '';
                }

                // credit replenish receiver id - required if true
                m.CreditReplenishId.err = '';
                if (m.CreditReplenish.val == 'true') {
                    m.CreditReplenishId.err = validText(m.CreditReplenishId.val, 1, 30);
                } else {
                    m.CreditReplenishId.val = '';
                }
            } else {
                // clear out EDI propeerties
                clearValidPropText(m.SenderId);
                clearValidPropText(m.ReceiverId);
                clearValidPropText(m.CSOSReceiverId);
                clearValidPropText(m.CSOSSenderId);
                clearValidPropText(m.CreditReplenishId);

                clearValidRadioText(m.CSOSSplit, 'false');
                clearValidRadioText(m.ShortPONames, 'false');
            }

            // PBM: cash card BINs array, 1 required
            m.PBMBINItems.err = '';
            if (m.Type.val == 'PBM') {
                if (Array.isArray(m.PBMBINItems.val) && m.PBMBINItems.val.length < 1) {
                    m.PBMBINItems.err = 'At least 1 BIN required';
                }

                const bins = m.PBMBINItems.val;
                let textFail = false;
                let numFail = false;

                // parse bins
                for (let i = 0; i < bins.length; i++) {
                    if (validText(bins[i], 2, 10).length > 0) {
                        textFail = true;
                    }
                    if (!textFail && !validSvc.isInteger(bins[i])) {
                        numFail = true;
                    }
                }

                // check for errors
                if (textFail) {
                    m.PBMBINItems.err = 'entries must be min 2 to 10 characters';
                } else if (numFail) {
                    m.PBMBINItems.err = 'entries must be numbers only';
                }

                // extract interval - required
                m.PBMExtractInterval.err = validText(m.PBMExtractInterval.val);
                // benefit level - required
                m.PBMBenefitLevel.err = validText(m.PBMBenefitLevel.val);
            } else {
                m.PBMBINItems.val = [];
                clearValidPropDroplist(m.PBMExtractInterval);
                clearValidPropDroplist(m.PBMBenefitLevel);
            }

            // WHOLESALER: excluded vin list, optional, but if present must be valid
            m.WholesalerExcludedVINs.err = '';
            if (m.Type.val == 'WHOLESALER') {
                const vins = Array.isArray(m.WholesalerExcludedVINs.val) ? m.WholesalerExcludedVINs.val : [];
                for (var i = 0; i < vins.length; i++) {
                    if (validText(vins[i], 2, 20).length > 0) {
                        m.WholesalerExcludedVINs.err = 'each VIN must be 2 to 20 characters';
                    }
                }
            }

            // AI AGENT: return any model validation errors where the .err property is set or checked
            // please note any properties on the model that are not validated here:
            result =
                m.Id.err == '' &&
                m.Name.err == '' &&
                m.SenderId.err == '' &&
                m.ReceiverId.err == '' &&
                m.CSOSReceiverId.err == '' &&
                m.CSOSSenderId.err == '' &&
                m.CreditReplenishId.err == '' &&
                m.PBMBINItems.err == '' &&
                m.WholesalerExcludedVINs.err == '' &&
                m.PBMExtractInterval.err == '' &&
                m.PBMBenefitLevel.err == '';

            // AI AGENT: notes for missing validations
            // Model Properties NOT being validated for .err:
            // - Pharmacies not editable
            // - KeyStr - server validated
            // - ProxyVendor - not validated Dropdown, optional
            // - ObjectState (not validated - system field)
            // - ShortPONames (radio button - cleared/set in logic, no validation needed)
            // - CSOSSplit (radio button - used in logic, no validation needed)
            // - CreditReplenish (radio button - used in logic, no validation needed)
        });

        return result;
    }, [state.mode, setModel]);

    const submit = useCallback((): boolean => {
        if (waiting) {
            return false;
        }

        if (!modelValid()) {
            ngDelegate.ok('Cannot Save', 'Check properties');
            return false;
        }

        // there was a lot of overlap below and missing validations
        // i converted the existing validation from Angular + consolidating all of these into modelValid above
        // if (model.Type.val === 'WHOLESALER') {
        //     if (!validateWholesaler()) {
        //         ngDelegate.ok('Cannot Save', 'Check properties');
        //         return false;
        //     }
        // } else if (model.Type.val === 'MANUFACTURER') {
        //     if (!validateManufacturer()) {
        //         ngDelegate.ok('Cannot Save', 'Check properties');
        //         return false;
        //     }
        // } else if (model.Type.val === 'PBM') {
        //     if (!validatePBM()) {
        //         ngDelegate.ok('Cannot Save', 'Check properties');
        //         return false;
        //     }
        // } else if (model.Type.val === 'TPA') {
        //     if (!validateTPA()) {
        //         ngDelegate.ok('Cannot Save', 'Check properties');
        //         return false;
        //     }
        // }

        submitAsync();
        return true;
    }, [submitAsync, waiting, modelValid]);

    return {
        model,
        setPropText,
        setPropList,
        submit,
        waiting,
        setWait,
        cancelEdit,
    };
};
